import React, { useState } from 'react';
import { useSupportTickets, SupportTicket } from '@/shared/hooks/useSupportTickets';
import { useAuth } from '@/shared/hooks/useAuth';
import { MessageSquare, Clock, CheckCircle2, AlertCircle, X, ArrowLeft } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useNavigate } from 'react-router-dom';

const MyInquiriesPage: React.FC = () => {
    const { user } = useAuth();
    const { tickets, loading } = useSupportTickets(user?.uid);
    const navigate = useNavigate();
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return { icon: Clock, text: '답변 대기 중', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' };
            case 'in-progress':
                return { icon: AlertCircle, text: '처리 중', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' };
            case 'answered':
                return { icon: CheckCircle2, text: '답변 완료', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' };
            case 'closed':
                return { icon: CheckCircle2, text: '종료', color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
            default:
                return { icon: Clock, text: '대기 중', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' };
        }
    };

    const getCategoryLabel = (type: string) => {
        switch (type) {
            case 'General': return '일반 문의';
            case 'Account': return '계정/인증';
            case 'Finance': return '입출금/거래';
            case 'Technical': return '기술 오류';
            default: return type;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-muted-foreground">로딩 중...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24 text-foreground">
            <div className="max-w-4xl mx-auto px-4 lg:px-8 space-y-8 pt-8">
                {/* Header */}
                <div className="space-y-4">
                    <button
                        onClick={() => navigate('/support')}
                        className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        고객지원으로 돌아가기
                    </button>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight">내 문의 내역</h1>
                        <p className="text-sm text-muted-foreground font-bold mt-2">제출한 문의 내역과 답변을 확인할 수 있습니다.</p>
                    </div>
                </div>

                {/* Tickets List */}
                {tickets.length === 0 ? (
                    <div className="p-12 text-center bg-card border border-border rounded-3xl">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground font-bold">제출한 문의가 없습니다.</p>
                        <button
                            onClick={() => navigate('/support')}
                            className="mt-6 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black text-sm uppercase transition-all"
                        >
                            문의하기
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {tickets.map(ticket => {
                            const StatusIcon = getStatusBadge(ticket.status).icon;
                            return (
                                <div
                                    key={ticket.id}
                                    onClick={() => setSelectedTicket(ticket)}
                                    className="p-6 bg-card border border-border rounded-2xl hover:border-emerald-500/30 transition-all cursor-pointer group"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 space-y-3">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="px-3 py-1 rounded-full text-xs font-black bg-blue-500/10 text-blue-500 border border-blue-500/20 uppercase">
                                                    {getCategoryLabel(ticket.type)}
                                                </span>
                                                <span className={cn("px-3 py-1 rounded-full text-xs font-black uppercase border flex items-center gap-1.5", getStatusBadge(ticket.status).color)}>
                                                    <StatusIcon className="h-3 w-3" />
                                                    {getStatusBadge(ticket.status).text}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-black group-hover:text-emerald-500 transition-colors">{ticket.subject}</h3>
                                            <p className="text-sm text-muted-foreground line-clamp-2">{ticket.content}</p>
                                            <div className="text-xs text-muted-foreground font-bold">
                                                {ticket.createdAt?.toDate().toLocaleString('ko-KR')}
                                            </div>
                                        </div>
                                        {ticket.status === 'answered' && (
                                            <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedTicket && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-card border border-border rounded-3xl w-full max-w-2xl p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-black uppercase">문의 상세 및 답변</h2>
                            <button
                                onClick={() => setSelectedTicket(null)}
                                className="h-10 w-10 rounded-xl hover:bg-accent flex items-center justify-center"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Ticket Details */}
                        <div className="space-y-4 p-6 bg-accent/30 rounded-2xl border border-border">
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 rounded-full text-xs font-black bg-blue-500/10 text-blue-500 border border-blue-500/20 uppercase">
                                    {getCategoryLabel(selectedTicket.type)}
                                </span>
                                <span className={cn("px-3 py-1 rounded-full text-xs font-black uppercase border flex items-center gap-1.5", getStatusBadge(selectedTicket.status).color)}>
                                    {getStatusBadge(selectedTicket.status).text}
                                </span>
                            </div>
                            <h3 className="text-xl font-black">{selectedTicket.subject}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{selectedTicket.content}</p>
                            <div className="pt-4 border-t border-border text-xs text-muted-foreground font-bold">
                                제출일: {selectedTicket.createdAt?.toDate().toLocaleString('ko-KR')}
                            </div>
                        </div>

                        {/* Admin Reply */}
                        {selectedTicket.adminReply ? (
                            <div className="space-y-3 p-6 bg-emerald-500/5 rounded-2xl border border-emerald-500/20">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                    <span className="text-xs font-black text-emerald-500 uppercase">답변 완료</span>
                                    <span className="text-xs text-muted-foreground ml-auto">
                                        {selectedTicket.adminRepliedAt?.toDate().toLocaleString('ko-KR')}
                                    </span>
                                </div>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedTicket.adminReply}</p>
                                <p className="text-xs text-muted-foreground font-bold">답변자: {selectedTicket.adminName}</p>
                            </div>
                        ) : (
                            <div className="p-6 bg-amber-500/5 rounded-2xl border border-amber-500/20 text-center">
                                <Clock className="h-8 w-8 mx-auto mb-3 text-amber-500" />
                                <p className="text-sm font-bold text-amber-600">답변 대기 중입니다.</p>
                                <p className="text-xs text-muted-foreground mt-2">담당자 확인 후 빠른 시일 내에 답변 드리겠습니다.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyInquiriesPage;
